import { useApiClient } from '../../hooks/useApiClient'
import type { components } from '@/types/api/v1'

type TrendReq = components['schemas']['TrendReq']
type SupplierRecSnapshot = components['schemas']['SupplierRecSnapshot']

interface UseSupplierRecDashboardTrendsParams {
  today?: Date
  enabled?: boolean
}

interface UseSupplierRecDashboardTrendsOptions {
  refetchInterval?: number
  refetchIntervalInBackground?: boolean
}

/**
 * Hook for fetching supplier reconciliation dashboard trends
 * Following the established pattern from other hooks in this folder
 */
export const useSupplierRecDashboardTrends = () => {
  const $api = useApiClient()

  const useSupplierRecDashboardTrendsQuery = (
    params: UseSupplierRecDashboardTrendsParams = {},
    options: UseSupplierRecDashboardTrendsOptions = {}
  ) => {
    const { today = new Date(), enabled = true } = params
    const { refetchInterval, refetchIntervalInBackground = false } = options

    // Format date as UTC YYYY-MM-DD at midnight
    const formattedDate = new Date(today)
    // Ensure we're working with UTC time to avoid timezone issues
    const dateString = formattedDate.toISOString().split('T')[0] + 'T00:00:00.000Z'

    return $api.useQuery('post', '/supplier_rec/dashboard/trends', {
      body: {
        today: dateString,
      } as TrendReq,
      reactQuery: {
        enabled,
        refetchInterval,
        refetchIntervalInBackground,
        staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
        retry: false, // Don't retry on failure
      },
    })
  }

  return useSupplierRecDashboardTrendsQuery
}
